import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Star } from "lucide-react";
import { SectionHeading } from "@/components/site/ui";
import { useFetch } from "@/lib/useFetch";
import { buyPackage } from "@/lib/cart";
import { inr, track } from "@/lib/api";

export function PackageCards({ heading = true }) {
    const nav = useNavigate();
    const { data: packages } = useFetch("/packages", []);
    const go = (slug) => { buyPackage(slug); track("cta_click", { cta: "package_choose", slug }); nav("/checkout"); };

    return (
        <section id="packages" className="bg-teal-950 py-20 grain" data-testid="packages-section">
            <div className="container-x">
                {heading && (
                    <div className="mb-12 text-center flex flex-col items-center">
                        <SectionHeading center eyebrow="Packages" title="Choose a Growth Package"
                            subtitle="Transparent monthly plans with clear deliverables. No confusing pricing structures." />
                    </div>
                )}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch max-w-6xl mx-auto">
                    {(packages || []).map((p, i) => (
                        <motion.div key={p.slug} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}
                            className={`relative rounded-2xl p-7 flex flex-col ${p.popular ? "bg-gradient-to-b from-teal-700 to-teal-800 border-2 border-gold shadow-[0_0_40px_rgba(229,195,120,0.2)]" : "card-teal"}`}
                            data-testid={`package-card-${p.slug}`}>
                            {p.badge && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-teal-900 text-xs font-bold px-4 py-1 rounded-full flex items-center gap-1">
                                    <Star className="w-3 h-3 fill-teal-900" /> {p.badge}
                                </div>
                            )}
                            <h3 className="text-lg font-bold text-cream">{p.name}</h3>
                            <p className="text-sm text-cream-soft mt-1 min-h-[40px]">{p.tagline}</p>
                            <div className="mt-4 flex items-baseline gap-1">
                                <span className="font-mono font-bold text-4xl text-cream">{inr(p.price)}</span>
                                <span className="text-cream-soft text-sm">{p.billing}</span>
                            </div>
                            <ul className="mt-6 space-y-2.5 flex-1">
                                {p.includes.map((f) => (
                                    <li key={f} className="flex items-start gap-2 text-sm text-cream-soft"><Check className="w-4 h-4 text-growth mt-0.5 shrink-0" /> {f}</li>
                                ))}
                            </ul>
                            <button onClick={() => go(p.slug)} className={`mt-6 rounded-full px-5 py-3 text-sm font-bold ${p.popular ? "btn-gold" : "btn-ghost-cream"}`} data-testid={`package-choose-${p.slug}`}>
                                {p.cta}
                            </button>
                        </motion.div>
                    ))}
                </div>
                <p className="text-center text-xs text-cream-soft/60 mt-8 max-w-2xl mx-auto">Prices exclusive of GST. We focus on transparent deliverables and professional execution — not unrealistic guarantees.</p>
            </div>
        </section>
    );
}
